import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TasksResponse, Template } from "./types";
import { RootState } from "index";
import { StudyTaskCategory } from "../../pages/elevate/studyTaskTemplates/types";

interface StudyTaskTemplate {
    currentOpenTemplate: Template | null;
    openTemplateCreationDialog: boolean;
    moduleId: number | null;
    partId: number | null;
    category: StudyTaskCategory | null;
    partAssignmentTemplates: Array<Template> | null;
    moduleAssignmentTemplates: Array<Template> | null;
    packUid: number | null;
    isEditMode: boolean;
    partTasks: TasksResponse | null;
    moduleTasks: TasksResponse | null;
    isLoadingPartTasks: boolean;
    isLoadingModuleTasks: boolean;
    currentBatchId: number | null;
    currentPackUid: number | null;
}

interface StudyTaskCreationPayload {
    category: StudyTaskCategory;
    moduleId: number;
    packUid: number;
    partId?: number;
    isEditMode?: boolean;
}

const initialState: StudyTaskTemplate = {
    openTemplateCreationDialog: false,
    currentOpenTemplate: null,
    moduleId: null,
    partId: null,
    category: null,
    partAssignmentTemplates: null,
    moduleAssignmentTemplates: null,
    partTasks: null,
    moduleTasks: null,
    packUid: null,
    isEditMode: false,
    isLoadingPartTasks: false,
    isLoadingModuleTasks: false,
    currentBatchId: null,
    currentPackUid: null,
}

const studyTaskTemplateSlice = createSlice({
    name: 'studyTaskTemplate',
    initialState,
    reducers: {
        openTemplateCreationDialog: (state, action: PayloadAction<StudyTaskCreationPayload>) => {
            state.openTemplateCreationDialog = true;
            state.category = action.payload.category;
            state.moduleId = action.payload.moduleId;
            state.packUid = action.payload.packUid;
            state.partId = action.payload.partId || null;
            state.isEditMode = action.payload.isEditMode || false;
        },
        closeTemplateCreationModal: (state) => {
            state.openTemplateCreationDialog = false;
            state.category = null;
            state.moduleId = null;
            state.partId = null;
            state.packUid = null;
            state.isEditMode = false;
        },
        setCurrentOpenTemplate: (state, action: PayloadAction<Template>) => {
            state.currentOpenTemplate = action.payload;
        },
        setPartAssignmentTemplates: (state, action: PayloadAction<Array<Template>>) => {
            state.partAssignmentTemplates = action.payload;
        },
        setModuleAssignmentTemplates: (state, action: PayloadAction<Array<Template>>) => {
            state.moduleAssignmentTemplates = action.payload;
        },
        setPartTasks: (state, action: PayloadAction<TasksResponse>) => {
            state.partTasks = action.payload;
        },
        setModuleTasks: (state, action: PayloadAction<TasksResponse>) => {
            state.moduleTasks = action.payload;
        },
        setPartTasksLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoadingPartTasks = action.payload;
        },
        setModuleTasksLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoadingModuleTasks = action.payload;
        },
        clearTasks: (state) => {
            state.partTasks = null;
            state.moduleTasks = null;
        },
    }
})

export const { 
    openTemplateCreationDialog, 
    closeTemplateCreationModal, 
    setPartAssignmentTemplates, 
    setModuleAssignmentTemplates,
    setCurrentOpenTemplate,
    setPartTasks,
    setModuleTasks,
    setPartTasksLoading,
    setModuleTasksLoading,
    clearTasks,
} = studyTaskTemplateSlice.actions;

export const selectStudyTaskTemplate = (state: RootState) => state.studyTaskTemplate;

export default studyTaskTemplateSlice.reducer;